import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, Wrench, RotateCcw, ChevronUp, ChevronDown, Package, Users } from 'lucide-react';
import { returnsRepairsService } from '../../api/services/returnsRepairsService';
import { MainLayout } from '../../components/layout/MainLayout';

/* ─── DARK MODE HOOK ───────────────────────────────────────────────────── */

const useIsDark = () => {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
};

/* ─── CONSTANTS ────────────────────────────────────────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* ─── SHARED COMPONENTS ────────────────────────────────────────────────── */

const YearSelect: React.FC<{ value: number; onChange: (y: number) => void; label: string }> = ({ value, onChange, label }) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{label}</span>
      <select
        value={value}
        onChange={e => onChange(parseInt(e.target.value))}
        className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
};

const MonthSelect: React.FC<{ value: number; onChange: (m: number) => void }> = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Month:</span>
    <select
      value={value}
      onChange={e => onChange(parseInt(e.target.value))}
      className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      <option value={0}>All Months</option>
      {MONTHS.map((name, i) => (
        <option key={i + 1} value={i + 1}>{name}</option>
      ))}
    </select>
  </div>
);

const Card: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  controls?: React.ReactNode;
}> = ({ title, icon, children, controls }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-2 text-gray-800 dark:text-white font-semibold text-sm">
        {icon}{title}
      </div>
      {controls && <div className="flex items-center gap-3">{controls}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Skeleton: React.FC<{ h?: string }> = ({ h = 'h-64' }) => (
  <div className={`${h} rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse`} />
);

const CountTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

/* ─── KPI SUMMARY ROW ──────────────────────────────────────────────────── */

const KpiRow: React.FC<{ primaryYear: number; compareYear: number }> = ({ primaryYear, compareYear }) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['returns-repairs', 'summary', primaryYear, compareYear],
    queryFn: () => returnsRepairsService.getSummary(primaryYear, compareYear),
    staleTime: 5 * 60 * 1000,
  });

  const current  = data.find(r => r.Year === primaryYear);
  const previous = data.find(r => r.Year === compareYear);

  const diff = (cur: number | undefined, prev: number | undefined) =>
    prev && prev > 0 ? (((cur ?? 0) - prev) / prev) * 100 : null;

  const kpis = [
    {
      label: 'Total Repairs',
      value: current?.TotalRepairs ?? 0,
      d: diff(current?.TotalRepairs, previous?.TotalRepairs),
      icon: <Wrench size={20} className="text-amber-500" />,
    },
    {
      label: 'Total Returns',
      value: current?.TotalReturns ?? 0,
      d: diff(current?.TotalReturns, previous?.TotalReturns),
      icon: <RotateCcw size={20} className="text-indigo-500" />,
    },
    {
      label: 'Combined Total',
      value: current?.TotalCombined ?? 0,
      d: diff(current?.TotalCombined, previous?.TotalCombined),
      icon: <TrendingUp size={20} className="text-gray-500" />,
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => <Skeleton key={i} h="h-24" />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {kpis.map(kpi => (
        <div key={kpi.label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{kpi.label}</span>
            {kpi.icon}
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{kpi.value.toLocaleString()}</p>
          {kpi.d !== null && (
            <div className={`flex items-center gap-1 mt-2 text-sm font-medium ${kpi.d >= 0 ? 'text-red-500' : 'text-emerald-500'}`}>
              {kpi.d >= 0 ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <span>{Math.abs(kpi.d).toFixed(1)}% vs {compareYear}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ─── MONTHLY CHART ────────────────────────────────────────────────────── */

type ChartType = 'repairs' | 'returns';

const MonthlyChart: React.FC<{ type: ChartType }> = ({ type }) => {
  const isDark = useIsDark();
  const tickColor = isDark ? '#ffffff' : '#6b7280';

  const currentYear = new Date().getFullYear();
  const [primaryYear, setPrimaryYear] = useState(currentYear);
  const [compareYear, setCompareYear] = useState(currentYear - 1);

  const { data = [], isLoading, isError } = useQuery({
    queryKey: ['returns-repairs', 'monthly-counts', primaryYear, compareYear],
    queryFn: () => returnsRepairsService.getMonthlyCounts(primaryYear, compareYear),
    staleTime: 5 * 60 * 1000,
  });

  const countKey = type === 'repairs' ? 'RepairCount' : 'ReturnCount';

  const chartData = useMemo(() => {
    const byKey: Record<string, Record<number, number>> = {};
    for (const row of data) {
      const k = String(row.Month);
      if (!byKey[k]) byKey[k] = {};
      byKey[k][row.Year] = row[countKey];
    }
    return MONTHS.map((month, i) => ({
      month,
      [primaryYear]: byKey[String(i + 1)]?.[primaryYear] ?? 0,
      [compareYear]: byKey[String(i + 1)]?.[compareYear] ?? 0,
    }));
  }, [data, primaryYear, compareYear, countKey]);

  const totals = useMemo(() => {
    const t1 = data.filter(r => r.Year === primaryYear).reduce((s, r) => s + r[countKey], 0);
    const t2 = data.filter(r => r.Year === compareYear).reduce((s, r) => s + r[countKey], 0);
    const diff = t2 > 0 ? ((t1 - t2) / t2) * 100 : 0;
    return { t1, t2, diff };
  }, [data, primaryYear, compareYear, countKey]);

  const isRepairs = type === 'repairs';
  const primaryColor = isRepairs ? '#f59e0b' : '#6366f1';
  const title = isRepairs ? 'Monthly Repairs — Year Over Year' : 'Monthly Returns — Year Over Year';
  const icon = isRepairs
    ? <Wrench size={16} className="text-amber-500" />
    : <RotateCcw size={16} className="text-indigo-500" />;

  return (
    <Card
      title={title}
      icon={icon}
      controls={
        <>
          <YearSelect value={compareYear} onChange={setCompareYear} label="Compare:" />
          <YearSelect value={primaryYear} onChange={setPrimaryYear} label="Year:" />
        </>
      }
    >
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className={`rounded-lg p-3 ${isRepairs ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-indigo-50 dark:bg-indigo-900/20'}`}>
          <p className={`text-xs font-medium ${isRepairs ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400'}`}>
            {primaryYear} Total
          </p>
          <p className={`text-lg font-bold mt-0.5 ${isRepairs ? 'text-amber-700 dark:text-amber-300' : 'text-indigo-700 dark:text-indigo-300'}`}>
            {totals.t1.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{compareYear} Total</p>
          <p className="text-lg font-bold text-gray-700 dark:text-gray-200 mt-0.5">{totals.t2.toLocaleString()}</p>
        </div>
        <div className={`rounded-lg p-3 ${totals.diff >= 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'}`}>
          <p className={`text-xs font-medium ${totals.diff >= 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            YoY Change
          </p>
          <div className="flex items-center gap-1 mt-0.5">
            {totals.diff >= 0
              ? <ChevronUp size={18} className="text-red-600 dark:text-red-400" />
              : <ChevronDown size={18} className="text-emerald-600 dark:text-emerald-400" />}
            <p className={`text-lg font-bold ${totals.diff >= 0 ? 'text-red-700 dark:text-red-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
              {Math.abs(totals.diff).toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      {isLoading ? <Skeleton h="h-72" /> : isError ? (
        <div className="h-72 flex items-center justify-center text-red-500 text-sm">Failed to load data</div>
      ) : (
        <ResponsiveContainer width="100%" height={288}>
          <LineChart data={chartData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: tickColor }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: tickColor }} width={48} />
            <Tooltip content={<CountTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey={primaryYear} stroke={primaryColor} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey={compareYear} stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

/* ─── BREAKDOWN TABLES ─────────────────────────────────────────────────── */

type ViewMode = 'year' | 'month' | 'week';

// Returns SQL Server-compatible week number (Sunday-based, matches DATEPART(WEEK,...))
const getSqlWeek = (date: Date): number => {
  const jan1 = new Date(date.getFullYear(), 0, 1);
  return Math.ceil(((date.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7);
};

// Human-readable date range for a given week number
const getWeekRange = (year: number, week: number): string => {
  const jan1 = new Date(year, 0, 1);
  const startOffset = (week - 1) * 7 - jan1.getDay();
  const start = new Date(year, 0, 1 + startOffset);
  const end   = new Date(start); end.setDate(start.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
};

const WeekSelect: React.FC<{ year: number; value: number; onChange: (w: number) => void }> = ({ year, value, onChange }) => {
  const weeks = Array.from({ length: 52 }, (_, i) => i + 1);
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Week:</span>
      <select
        value={value}
        onChange={e => onChange(parseInt(e.target.value))}
        className="text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {weeks.map(w => (
          <option key={w} value={w}>Week {w} ({getWeekRange(year, w)})</option>
        ))}
      </select>
    </div>
  );
};

const BreakdownTables: React.FC = () => {
  const now = new Date();
  const currentYear  = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentWeek  = getSqlWeek(now);

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [year,  setYear]  = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [week,  setWeek]  = useState(currentWeek);
  const [partFilter,    setPartFilter]    = useState<'all' | 'returns' | 'repairs'>('all');
  const [patternFilter, setPatternFilter] = useState<'all' | 'returns' | 'repairs'>('all');

  // Resolve query params based on viewMode
  const queryMonth = viewMode === 'month' ? month : 0;
  const queryWeek  = viewMode === 'week'  ? week  : 0;

  const { data: partData = [], isLoading: partsLoading } = useQuery({
    queryKey: ['returns-repairs', 'part-numbers', year, queryMonth, queryWeek],
    queryFn: () => returnsRepairsService.getPartNumberBreakdown(year, queryMonth, queryWeek),
    staleTime: 5 * 60 * 1000,
  });

  const { data: custData = [], isLoading: custsLoading } = useQuery({
    queryKey: ['returns-repairs', 'customers', year, queryMonth, queryWeek],
    queryFn: () => returnsRepairsService.getCustomerBreakdown(year, queryMonth, queryWeek),
    staleTime: 5 * 60 * 1000,
  });

  const filteredPartData = useMemo(() => {
    if (partFilter === 'returns') {
      return [...partData]
        .filter(r => r.ReturnCount > 0)
        .sort((a, b) => b.ReturnCount - a.ReturnCount);
    }
    if (partFilter === 'repairs') {
      return [...partData]
        .filter(r => r.RepairCount > 0)
        .sort((a, b) => b.RepairCount - a.RepairCount);
    }
    return partData; // already sorted by TotalCount desc from backend
  }, [partData, partFilter]);

  // Aggregate part numbers → pattern numbers (first 5 chars) client-side
  const patternData = useMemo(() => {
    const agg: Record<string, { ReturnCount: number; RepairCount: number }> = {};
    for (const row of partData) {
      const pattern = row.PartNumber.substring(0, 5).toUpperCase();
      if (!agg[pattern]) agg[pattern] = { ReturnCount: 0, RepairCount: 0 };
      agg[pattern].ReturnCount += row.ReturnCount;
      agg[pattern].RepairCount += row.RepairCount;
    }
    return Object.entries(agg).map(([PatternNumber, c]) => ({
      PatternNumber,
      ReturnCount: c.ReturnCount,
      RepairCount: c.RepairCount,
      TotalCount: c.ReturnCount + c.RepairCount,
    })).sort((a, b) => b.TotalCount - a.TotalCount);
  }, [partData]);

  const filteredPatternData = useMemo(() => {
    if (patternFilter === 'returns') {
      return [...patternData]
        .filter(r => r.ReturnCount > 0)
        .sort((a, b) => b.ReturnCount - a.ReturnCount);
    }
    if (patternFilter === 'repairs') {
      return [...patternData]
        .filter(r => r.RepairCount > 0)
        .sort((a, b) => b.RepairCount - a.RepairCount);
    }
    return patternData;
  }, [patternData, patternFilter]);

  const maxPatternCount = (() => {
    if (patternFilter === 'returns') return filteredPatternData[0]?.ReturnCount || 1;
    if (patternFilter === 'repairs') return filteredPatternData[0]?.RepairCount || 1;
    return filteredPatternData[0]?.TotalCount || 1;
  })();

  const periodLabel = viewMode === 'week'
    ? `Week ${week} (${getWeekRange(year, week)}, ${year})`
    : viewMode === 'month'
      ? `${MONTHS[month - 1]} ${year}`
      : String(year);

  const activePartCount = partFilter === 'returns'
    ? (filteredPartData[0]?.ReturnCount ?? 1)
    : partFilter === 'repairs'
      ? (filteredPartData[0]?.RepairCount ?? 1)
      : (filteredPartData[0]?.TotalCount ?? 1);

  const maxPartTotal = activePartCount || 1;
  const maxCustTotal = custData[0]?.TotalCount ?? 1;

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Breakdown Analysis</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Part numbers and customers driving returns &amp; repairs — {periodLabel}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View mode toggle */}
          <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden text-xs font-medium">
            {(['year', 'month', 'week'] as ViewMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 capitalize transition-colors ${
                  viewMode === mode
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
          {/* Contextual pickers */}
          <YearSelect value={year} onChange={setYear} label="Year:" />
          {viewMode === 'month' && <MonthSelect value={month} onChange={setMonth} />}
          {viewMode === 'week'  && <WeekSelect year={year} value={week} onChange={setWeek} />}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* Part Numbers Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
            <Package size={16} className="text-indigo-500" />
            <span className="font-semibold text-sm text-gray-800 dark:text-white">Part Numbers</span>
            {/* Filter toggles */}
            <div className="ml-3 flex items-center rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden text-xs font-medium">
              {(['all', 'returns', 'repairs'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setPartFilter(f)}
                  className={`px-2.5 py-1 transition-colors ${
                    partFilter === f
                      ? f === 'returns'
                        ? 'bg-indigo-600 text-white'
                        : f === 'repairs'
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-700 text-white dark:bg-gray-200 dark:text-gray-800'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'returns' ? 'Returns' : 'Repairs'}
                </button>
              ))}
            </div>
            {!partsLoading && (
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{filteredPartData.length} parts</span>
            )}
          </div>
          <div className="overflow-auto max-h-96">
            {partsLoading ? (
              <div className="p-5"><Skeleton h="h-48" /></div>
            ) : filteredPartData.length === 0 ? (
              <p className="p-5 text-sm text-gray-400 dark:text-gray-500 text-center">No data for this period</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Part #</th>
                    <th className={`text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wide ${partFilter === 'returns' ? 'text-indigo-600 dark:text-indigo-400' : 'text-indigo-500 dark:text-indigo-400'}`}>
                      Returns {partFilter === 'returns' && '▼'}
                    </th>
                    <th className={`text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wide ${partFilter === 'repairs' ? 'text-amber-600 dark:text-amber-400' : 'text-amber-500 dark:text-amber-400'}`}>
                      Repairs {partFilter === 'repairs' && '▼'}
                    </th>
                    <th className={`text-right px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${partFilter === 'all' ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>
                      Total {partFilter === 'all' && '▼'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredPartData.map((row, i) => {
                    const barCount = partFilter === 'returns' ? row.ReturnCount : partFilter === 'repairs' ? row.RepairCount : row.TotalCount;
                    const barColor = partFilter === 'repairs' ? 'bg-amber-400 dark:bg-amber-500' : 'bg-indigo-400 dark:bg-indigo-500';
                    return (
                      <tr key={row.PartNumber} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 dark:text-gray-500 w-5 text-right">{i + 1}</span>
                            <span className="font-mono font-medium text-gray-900 dark:text-white">{row.PartNumber}</span>
                          </div>
                          <div className="mt-1 ml-7 h-1 rounded-full bg-gray-100 dark:bg-gray-700">
                            <div
                              className={`h-1 rounded-full ${barColor}`}
                              style={{ width: `${Math.round((barCount / maxPartTotal) * 100)}%` }}
                            />
                          </div>
                        </td>
                        <td className="text-center px-3 py-2.5">
                          <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${partFilter === 'returns' ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 ring-1 ring-indigo-300 dark:ring-indigo-600' : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'}`}>
                            {row.ReturnCount}
                          </span>
                        </td>
                        <td className="text-center px-3 py-2.5">
                          <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${partFilter === 'repairs' ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 ring-1 ring-amber-300 dark:ring-amber-600' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}>
                            {row.RepairCount}
                          </span>
                        </td>
                        <td className={`text-right px-4 py-2.5 font-bold ${partFilter === 'all' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>{row.TotalCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
            <Users size={16} className="text-indigo-500" />
            <span className="font-semibold text-sm text-gray-800 dark:text-white">Customers</span>
            {!custsLoading && (
              <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{custData.length} customers</span>
            )}
          </div>
          <div className="overflow-auto max-h-96">
            {custsLoading ? (
              <div className="p-5"><Skeleton h="h-48" /></div>
            ) : custData.length === 0 ? (
              <p className="p-5 text-sm text-gray-400 dark:text-gray-500 text-center">No data for this period</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Customer</th>
                    <th className="text-center px-3 py-2.5 text-xs font-medium text-indigo-500 dark:text-indigo-400 uppercase tracking-wide">Returns</th>
                    <th className="text-center px-3 py-2.5 text-xs font-medium text-amber-500 dark:text-amber-400 uppercase tracking-wide">Repairs</th>
                    <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {custData.map((row, i) => (
                    <tr key={row.CustomerName} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 dark:text-gray-500 w-5 text-right">{i + 1}</span>
                          <span className="font-medium text-gray-900 dark:text-white truncate max-w-[200px]" title={row.CustomerName}>
                            {row.CustomerName}
                          </span>
                        </div>
                        {/* mini bar */}
                        <div className="mt-1 ml-7 h-1 rounded-full bg-gray-100 dark:bg-gray-700">
                          <div
                            className="h-1 rounded-full bg-indigo-400 dark:bg-indigo-500"
                            style={{ width: `${Math.round((row.TotalCount / maxCustTotal) * 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className="text-center px-3 py-2.5">
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                          {row.ReturnCount}
                        </span>
                      </td>
                      <td className="text-center px-3 py-2.5">
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                          {row.RepairCount}
                        </span>
                      </td>
                      <td className="text-right px-4 py-2.5 font-bold text-gray-900 dark:text-white">{row.TotalCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>

      {/* Pattern Number Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <Package size={16} className="text-indigo-500" />
          <span className="font-semibold text-sm text-gray-800 dark:text-white">Pattern Numbers</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">(first 5 chars of part #)</span>
          {/* Filter toggles */}
          <div className="ml-3 flex items-center rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden text-xs font-medium">
            {(['all', 'returns', 'repairs'] as const).map(f => (
              <button
                key={f}
                onClick={() => setPatternFilter(f)}
                className={`px-2.5 py-1 transition-colors ${
                  patternFilter === f
                    ? f === 'returns'
                      ? 'bg-indigo-600 text-white'
                      : f === 'repairs'
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-700 text-white dark:bg-gray-200 dark:text-gray-800'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {f === 'all' ? 'All' : f === 'returns' ? 'Returns' : 'Repairs'}
              </button>
            ))}
          </div>
          {!partsLoading && (
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{filteredPatternData.length} patterns</span>
          )}
        </div>
        <div className="overflow-auto max-h-80">
          {partsLoading ? (
            <div className="p-5"><Skeleton h="h-48" /></div>
          ) : filteredPatternData.length === 0 ? (
            <p className="p-5 text-sm text-gray-400 dark:text-gray-500 text-center">No data for this period</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/60">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Pattern #</th>
                  <th className={`text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wide ${patternFilter === 'returns' ? 'text-indigo-600 dark:text-indigo-400' : 'text-indigo-500 dark:text-indigo-400'}`}>
                    Returns {patternFilter === 'returns' && '▼'}
                  </th>
                  <th className={`text-center px-3 py-2.5 text-xs font-medium uppercase tracking-wide ${patternFilter === 'repairs' ? 'text-amber-600 dark:text-amber-400' : 'text-amber-500 dark:text-amber-400'}`}>
                    Repairs {patternFilter === 'repairs' && '▼'}
                  </th>
                  <th className={`text-right px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${patternFilter === 'all' ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>
                    Total {patternFilter === 'all' && '▼'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredPatternData.map((row, i) => {
                  const barCount = patternFilter === 'returns' ? row.ReturnCount : patternFilter === 'repairs' ? row.RepairCount : row.TotalCount;
                  const barColor = patternFilter === 'repairs' ? 'bg-amber-400 dark:bg-amber-500' : 'bg-indigo-400 dark:bg-indigo-500';
                  return (
                    <tr key={row.PatternNumber} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 dark:text-gray-500 w-5 text-right">{i + 1}</span>
                          <span className="font-mono font-semibold text-gray-900 dark:text-white tracking-wider">{row.PatternNumber}</span>
                        </div>
                        <div className="mt-1 ml-7 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700">
                          <div
                            className={`h-1.5 rounded-full ${barColor}`}
                            style={{ width: `${Math.round((barCount / maxPatternCount) * 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className="text-center px-3 py-2.5">
                        <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${patternFilter === 'returns' ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 ring-1 ring-indigo-300 dark:ring-indigo-600' : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'}`}>
                          {row.ReturnCount}
                        </span>
                      </td>
                      <td className="text-center px-3 py-2.5">
                        <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${patternFilter === 'repairs' ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 ring-1 ring-amber-300 dark:ring-amber-600' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'}`}>
                          {row.RepairCount}
                        </span>
                      </td>
                      <td className={`text-right px-4 py-2.5 font-bold ${patternFilter === 'all' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>{row.TotalCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

    </div>
  );
};

/* ─── PAGE ─────────────────────────────────────────────────────────────── */

const ReturnsRepairsReports: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [primaryYear, setPrimaryYear] = useState(currentYear);
  const [compareYear, setCompareYear] = useState(currentYear - 1);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Returns &amp; Repairs Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Year-over-year analysis of return authorizations</p>
          </div>
          <div className="flex items-center gap-3">
            <YearSelect value={compareYear} onChange={setCompareYear} label="Compare:" />
            <YearSelect value={primaryYear} onChange={setPrimaryYear} label="Year:" />
          </div>
        </div>

        <KpiRow primaryYear={primaryYear} compareYear={compareYear} />

        <MonthlyChart type="repairs" />
        <MonthlyChart type="returns" />

        <BreakdownTables />
      </div>
    </MainLayout>
  );
};

export default ReturnsRepairsReports;
